import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://pay.mojomoney.africa';
const API = `${BACKEND_URL}/api`;

/**
 * Phase 2 — lost-device recovery page.
 *
 * Two modes, distinguished by URL params:
 *   /recovery                  → "request recovery" form (user enters their email)
 *   /recovery?email=…&token=…  → "complete recovery" form (user lands here from the
 *                                 email link, enters OTP if their method is email_sms)
 *
 * On success we have a fresh session cookie + all old passkeys are
 * revoked, so we push the user to PasskeySettings to enrol a new one.
 */
const PasskeyRecoveryPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tokenFromUrl = params.get('token') || '';
  const emailFromUrl = params.get('email') || '';

  const mode = tokenFromUrl ? 'verify' : 'request';

  const [email, setEmail]   = useState(emailFromUrl);
  const [token, setToken]   = useState(tokenFromUrl);
  const [otp, setOtp]       = useState('');
  const [busy, setBusy]     = useState(false);
  const [sent, setSent]     = useState(false);

  useEffect(() => {
    if (tokenFromUrl) setToken(tokenFromUrl);
    if (emailFromUrl) setEmail(emailFromUrl);
  }, [tokenFromUrl, emailFromUrl]);

  const requestRecovery = async () => {
    if (!email) {
      toast.error('Email is required');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/webauthn/recovery/begin`, { email });
      setSent(true);
      toast.success('If that email is registered, check your inbox.');
    } catch (e) {
      // The backend returns 200 even for unknown emails to avoid leaking.
      toast.success('If that email is registered, check your inbox.');
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!email || !token) {
      toast.error('Email and token are both required');
      return;
    }
    setBusy(true);
    try {
      const body = { email, token };
      if (otp) body.otp = otp;
      await axios.post(`${API}/webauthn/recovery/verify`, body);
      toast.success('Recovery complete. Set up a new passkey now.');
      navigate('/customer/passkeys');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Recovery failed';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B0C34] via-[#2D1854] to-[#1B0C34] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-[#F2B10C] rounded-full flex items-center justify-center">
            <KeyRound size={32} className="text-[#1B0C34]" />
          </div>
          <CardTitle className="text-2xl text-[#1B0C34]">Passkey recovery</CardTitle>
          <CardDescription>
            Lost the device with your passkey? Recover access here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === 'request' && !sent && (
            <>
              <div>
                <Label htmlFor="rec-email">Email on your account</Label>
                <Input
                  id="rec-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="recovery-email-input"
                />
              </div>
              <Button
                onClick={requestRecovery}
                disabled={busy || !email}
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                data-testid="recovery-request-btn"
              >
                {busy ? 'Working…' : 'Send recovery instructions'}
              </Button>
            </>
          )}

          {mode === 'request' && sent && (
            <p className="text-sm text-slate-700">
              If your email is registered with Mojo Money and your account is
              in a recoverable state, you will receive instructions within a
              few minutes. The email itself will tell you which steps to
              follow (link only, link + SMS code, or contact support).
            </p>
          )}

          {mode === 'verify' && (
            <>
              <p className="text-sm text-slate-600">
                Enter the OTP we sent to your phone (only required if your
                account uses email + SMS recovery).
              </p>
              <div>
                <Label htmlFor="rec-email2">Email</Label>
                <Input
                  id="rec-email2"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="recovery-email-input"
                />
              </div>
              <div>
                <Label htmlFor="rec-otp">SMS OTP (if required)</Label>
                <Input
                  id="rec-otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6 digit code"
                  data-testid="recovery-otp-input"
                />
              </div>
              <Button
                onClick={verify}
                disabled={busy || !email || !token}
                className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
                data-testid="recovery-verify-btn"
              >
                {busy ? 'Verifying…' : 'Complete recovery'}
              </Button>
              <p className="text-xs text-slate-500">
                On success, ALL passkeys currently registered to this account will be revoked
                and you'll be asked to enrol a new one.
              </p>
            </>
          )}

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-[#145539] hover:underline"
            >
              <ArrowLeft className="inline w-3 h-3 mr-1" /> Back to login
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PasskeyRecoveryPage;
